import React, {useState} from 'react';
import {useRouter} from 'next/router';
import cx from 'classnames';
import {Disclosure} from '@headlessui/react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';

import {CodeIcon, VideoCameraIcon} from '@heroicons/react/solid';
import {ChevronIcon, SimpleLockIcon} from '../../components/svg';
import MDX from '../../components/MDX';
import {Tab} from '../../components/Tabs';
import YouTubeVideo from '../../components/YouTubeVideo';
import {useAnalytics} from '../../hooks';
import Markdown from './LessonMarkdown';
import LessonCommentSection from './LessonCommentSection';

const MAX_ANSWERS_SHOWN = 4;

const Accordion = ({
  name, content, defaultOpen, color = 'gray', onClick,
}) => (
  <Disclosure as="div" defaultOpen={defaultOpen} onClick={onClick}>
    {({open}) => (
      <div
        className={cx('rounded-lg my-4 transition duration-50 border-2',
          color === 'green' && 'bg-green-50 border-green-100',
          color === 'yellow' && 'bg-yellow-50 border-yellow-100')}
      >
        <dt className="text-base">
          <Disclosure.Button className="py-2 pl-4 pr-3 text-left w-full flex justify-between items-center text-gray-800">
            <span className={cx('font-medium', {
              'text-green-900': color === 'green',
              'text-yellow-800': color === 'yellow',
              'text-gray-800': color === 'gray',
            })}
            >
              {name}
            </span>
            <span className="ml-6 h-7 flex items-center">
              <ChevronIcon
                className={cx(open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform')}
                aria-hidden="true"
              />
            </span>
          </Disclosure.Button>
        </dt>
        <Disclosure.Panel as="div" className="pb-3 px-4" unmount={false}>
          <Markdown>{content}</Markdown>
        </Disclosure.Panel>
      </div>
    )}
  </Disclosure>
);

const InteractiveLesson = ({
  lesson,
  course,
  hideIntroWithoutAccess = true,
  onRender,
  commentData,
  initialDraft,
  onActiveTabChange = () => {}, // notify parent of tab change
}) => {
  const {tracker} = useAnalytics();
  const defaultOpen = lesson.isWebCrawler;
  const router = useRouter();

  const Section = {
    QUESTION: 'question',
    SOLUTION: 'solution',
    VIDEO: 'video',
    COMMUNITY: 'community',
  };

  const [activeTab, setActiveTab] = useState(Section.QUESTION);

  const onClickHint = () => {
    tracker.lessonClickHint({lesson: lesson.slug, course: course?.slug});
  };

  const onClickSolution = () => {
    if (!lesson.hasAccess) {
      router.push('/upgrade?src=lesson-solution');
      return;
    }
    setActiveTab(Section.SOLUTION);
    onActiveTabChange(Section.SOLUTION);
    tracker.lessonViewSolution({lesson: lesson.slug, course: course?.slug});
  };

  const onClickVideo = () => {
    if (!lesson.hasAccess) {
      router.push('/upgrade?src=lesson-video');
      return;
    }
    setActiveTab(Section.VIDEO);
    onActiveTabChange(Section.VIDEO);
    tracker.lessonViewVideoSolution({lesson: lesson.slug, course: course?.slug});
  };

  const onClickCommunity = () => {
    setActiveTab(Section.COMMUNITY);
    onActiveTabChange(Section.COMMUNITY);
    tracker.lessonViewCommunityAnswers({lesson: lesson.slug, course: course?.slug});
  };

  const comments = commentData?.root || [];

  return (
    <div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <div className="mt-2 -mb-px flex space-x-4" aria-label="Tabs">
          <Tab
            key={Section.QUESTION}
            onClick={() => {
              setActiveTab(Section.QUESTION);
              onActiveTabChange(Section.QUESTION);
            }}
            active={activeTab === Section.QUESTION}
            className
          >
            Question
          </Tab>
          {(lesson.content) && (
            <Tab
              key={Section.SOLUTION}
              onClick={onClickSolution}
              active={activeTab === Section.SOLUTION}
            >
              <OverlayTrigger
                placement="bottom"
                overlay={!lesson.hasAccess
                  ? <Tooltip>Solutions are only available to Exponent members</Tooltip>
                  : <></>}
              >
                <div>
                  {!lesson.hasAccess ? <SimpleLockIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" /> : <CodeIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" />}
                  Solution
                </div>
              </OverlayTrigger>
            </Tab>
          )}
          {(lesson.video) && (
            <Tab
              key={Section.VIDEO}
              onClick={onClickVideo}
              active={activeTab === Section.VIDEO}
            >
              <OverlayTrigger
                placement="bottom"
                overlay={!lesson.hasAccess
                  ? <Tooltip>Mock interviews are only available to Exponent members</Tooltip>
                  : <></>}
              >
                <div>
                  {!lesson.hasAccess ? <SimpleLockIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" /> : <VideoCameraIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" />}
                  Mock Interview
                </div>
              </OverlayTrigger>
            </Tab>
          )}

          {/* This won't show in Socrates question */}
          {lesson.questionId && (
            <Tab
              key={Section.COMMUNITY}
              onClick={onClickCommunity}
              active={activeTab === Section.COMMUNITY}
              count={comments?.length || 0}
            >
              Community
            </Tab>
          )}
        </div>
      </div>

      {/* Question */}
      <div className={cx({hidden: activeTab !== Section.QUESTION})}>
        {lesson.intro && (
          <div className={cx('prose prose-lg prose-indigo', {'LessonPage--blocked': hideIntroWithoutAccess && !lesson.hasAccess})}>
            <MDX onRender={onRender}>{lesson.intro}</MDX>
          </div>
        )}

        {/* Hints and Gotchas - may be text or object {hint/gotcha: string} */}

        {lesson?.hints?.map((hint, idx) => (
          <Accordion
            key={hint.hint || hint}
            name={`Hint ${idx + 1}`}
            content={hint.hint || hint}
            color="green"
            onClick={onClickHint}
            defaultOpen={defaultOpen}
          />
        ))}

        {lesson?.gotchas?.map((gotcha, idx) => (
          <Accordion
            key={gotcha.gotcha || gotcha}
            name={`Challenge ${idx + 1}`}
            content={gotcha.gotcha || gotcha}
            color="yellow"
            onClick={onClickHint}
            defaultOpen={defaultOpen}
          />
        ))}
      </div>

      {/* Solution */}
      <div className={cx({hidden: activeTab !== Section.SOLUTION})}>
        {lesson.hasAccess && (
          <div className="mt-6 prose prose-lg prose-indigo">
            <MDX onRender={onRender}>{lesson.content}</MDX>
          </div>
        )}
      </div>

      {/* Video Solution */}
      <div className={cx({hidden: activeTab !== Section.VIDEO})}>
        {lesson.hasAccess && lesson.video && (
          <div>
            <div className="rounded overflow-hidden mt-6">
              <YouTubeVideo
                videoID={lesson.video}
                onPlay={() => tracker.lessonPlayVideo({lesson: lesson.slug, course: course?.slug})}
              />
            </div>
            <div className="mt-6 prose prose-lg prose-indigo">
              <MDX onRender={onRender}>{lesson.description}</MDX>
            </div>
          </div>
        )}
      </div>

      {/* Community Answers */}
      <div className={cx('mt-4', {hidden: activeTab !== Section.COMMUNITY})}>
        {lesson.questionId && (
          <div>
            <LessonCommentSection
              questionID={lesson.questionId.toString()}
              commentData={commentData}
              numAnswers={MAX_ANSWERS_SHOWN}
              open={!!initialDraft}
              header="Community solutions"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveLesson;
